/**
 * Internal Sync-Config SETTINGS API Contract
 *
 * PATCH /internal/platform-data-sync/sync-config/settings — write a PARTIAL set
 * of tunables onto the per-(school, sync_type) `PlatformDataSyncConfig` row,
 * for ONE sync_type across a LIST of schools.
 *
 * WHY THIS EXISTS ALONGSIDE THE TOGGLE. `POST /internal/platform-data-sync/
 * sync-config` writes exactly one column (`is_enabled`) and nothing else, and
 * the Clerk-gated per-school PATCH writes the same one. Every OTHER tunable —
 * cadence, window, priority, a forced run — was reachable only through the
 * FLEET-WIDE platform-defaults writer, which edits the system default row and
 * pushes it to every school at once. So "slow this one school's assignment sync
 * down" had no API at all, and each new tunable would have needed its own route.
 * This endpoint closes that gap once: the set of writable settings is a REGISTRY
 * on the backend, and adding a tunable is one registry entry, not a new route.
 *
 * REGISTRY-BACKED, exactly like `/internal/config/flags`. `settings` is an
 * object of registered setting NAMES to values. An unregistered name is a 400
 * (no column can be written by a typo) and a value that fails its per-setting
 * schema is a 400 (no out-of-range cadence can be stored). The registry is also
 * the allowlist that keeps sync-OWNED columns — the run counters, the scheduler's
 * own timestamps, the year watermarks — permanently unwritable by operators.
 *
 * PARTIAL-PATCH SEMANTICS, and how they differ from the flags endpoint. A flag
 * write replaces its row's `value` object wholesale; these are COLUMNS, so only
 * the settings named in the request are written and every other column on the
 * row is left exactly as it was. Omitting `priority` does not clear it.
 *
 * `reason` is REQUIRED on every call, not only on a disable. This endpoint can
 * silently change how often we hit a vendor API for a live school; an unexplained
 * cadence change found weeks later is indistinguishable from a bug.
 *
 * BULK SEMANTICS mirror the toggle: per-school best-effort. An unresolvable
 * school (`resolved: false`) or one with no non-archived config for that
 * sync_type (`found: false`) is a per-school outcome, NOT a batch-level failure —
 * the call still returns HTTP 200. Only a malformed body or an unregistered /
 * invalid setting is a 400.
 */

import { z } from 'zod'
import { DisabledReasonCodeSchema, SyncConfigAuditSchema } from './sync-config-toggle'

/**
 * PATCH request body.
 *
 * NOTE the two-stage validation: this schema only proves `settings` is a
 * non-empty object of JSON values. WHICH names are legal, and what each value
 * may be, lives in the backend's setting registry and is applied by the
 * use-case — the same split `SetSystemFlagRequestSchema` uses, and for the same
 * reason: the applicable schema depends on the key, so it cannot be expressed
 * here without restating the registry in a second place that would drift.
 */
export const SyncConfigSettingsPatchRequestSchema = z
  .object({
    sync_type: z.string().min(1).describe('Sync type to write (e.g. ASSIGNMENTS, SECTION_ROSTER)'),
    school_ids: z
      .array(z.string().min(1))
      .min(1)
      .describe('Schools to apply the settings to (each a public ID (CUID) or slug)'),
    settings: z
      .record(z.string(), z.unknown())
      .refine(value => Object.keys(value).length > 0, {
        message: 'settings must name at least one registered setting'
      })
      .describe('Registered setting names to their new values; only the named settings are written'),
    reason: z.string().min(1).max(2000).describe('Audit reason — REQUIRED on every write, not only on a disable'),
    disabled_reason_code: DisabledReasonCodeSchema.optional().describe(
      'Machine-readable intent; REQUIRED when `settings.enabled` is false. Defaults to RETIRED when `settings.archived` is true'
    ),
    changed_by: z
      .string()
      .max(200)
      .optional()
      .describe("Optional caller identity for the audit trail (defaults to 'internal-api')")
  })
  // Same rule as the toggle endpoint: a disable that names no intent leaves a row
  // no detector can read. `archived: true` needs no code — an archived config is
  // retired by definition, and the writer fills in RETIRED (SCH-2012 KTD4).
  .refine(data => data.settings.enabled !== false || data.disabled_reason_code !== undefined, {
    message: 'disabled_reason_code is required when disabling a sync configuration',
    path: ['disabled_reason_code']
  })

export type SyncConfigSettingsPatchRequest = z.infer<typeof SyncConfigSettingsPatchRequestSchema>

/**
 * Per-school result of a settings PATCH.
 *
 * `previous` and `next` carry ONLY the settings this request named, keyed by
 * their registered names — so the response is a diff of what actually moved,
 * not an echo of the request and not a dump of the whole row. Both are null
 * when nothing was written (`resolved: false` or `found: false`).
 */
export const SyncConfigSettingsResultSchema = z.object({
  schoolId: z.string().describe('The school_id as supplied in the request (public id or slug)'),
  resolvedSchoolId: z.string().nullable().describe("The resolved school's public ID, or null if unresolved"),
  resolved: z.boolean().describe('Whether the school_id resolved to a school'),
  found: z.boolean().describe('Whether a non-archived config existed for the (school, sync_type)'),
  previous: z
    .record(z.string(), z.unknown())
    .nullable()
    .describe('The named settings as they were before the write (null when not found)'),
  next: z
    .record(z.string(), z.unknown())
    .nullable()
    .describe('The named settings as they are after the write (null when not found)'),
  changed: z
    .array(z.string())
    .describe('Registered names whose value actually differs from before — empty when the write was a no-op'),
  audit: SyncConfigAuditSchema.nullable().describe('Post-write audit fields (null when not found)')
})

export type SyncConfigSettingsResult = z.infer<typeof SyncConfigSettingsResultSchema>

/**
 * PATCH response. Aggregate counts plus the per-school diff array.
 */
export const SyncConfigSettingsPatchResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    syncType: z.string(),
    settings: z.record(z.string(), z.unknown()).describe('The validated settings that were applied'),
    totalRequested: z.number().int(),
    resolved: z.number().int().describe('Schools whose id resolved to a school row'),
    found: z.number().int().describe('Resolved schools that had a non-archived config for the sync_type'),
    changed: z.number().int().describe('Schools where at least one setting actually moved'),
    results: z.array(SyncConfigSettingsResultSchema)
  })
})

export type SyncConfigSettingsPatchResponse = z.infer<typeof SyncConfigSettingsPatchResponseSchema>

/**
 * One entry of the registry, as surfaced to callers by the discovery route.
 * Lets an operator (or an agent) ask what is writable rather than guessing a
 * name and getting a 400.
 */
export const SyncSettingDescriptorSchema = z.object({
  name: z.string().describe('Stable public name — what the PATCH body addresses'),
  description: z.string().describe('What the setting controls, in one or two sentences'),
  constraint: z.string().describe('Human-readable summary of what values are accepted')
})

export type SyncSettingDescriptor = z.infer<typeof SyncSettingDescriptorSchema>

/**
 * GET /internal/platform-data-sync/sync-config/settings — response. Lists the
 * registry so callers can discover the writable surface.
 */
export const ListSyncSettingsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    settings: z.array(SyncSettingDescriptorSchema)
  })
})

export type ListSyncSettingsResponse = z.infer<typeof ListSyncSettingsResponseSchema>
